import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, Lock, ArrowLeft } from "lucide-react";
import logo from "@/assets/logo.png";
import { supabase } from "@/integrations/supabase/client";

const ALLOWED_EMAIL_DOMAINS = [
  "@superiorformulations.com.au",
  "@test.qualityflow.dev",
];

function isAllowedEmail(email: string): boolean {
  return ALLOWED_EMAIL_DOMAINS.some((domain) => email.endsWith(domain));
}

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { user, signInWithEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  // Check for password recovery event
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setShowUpdatePassword(true);
      }
    });

    // Check URL hash for recovery token (fallback)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    if (hashParams.get("type") === "recovery") {
      setShowUpdatePassword(true);
    }

    return () => subscription.unsubscribe();
  }, []);

  // Redirect if already logged in (but not during password update)
  useEffect(() => {
    if (user && !showUpdatePassword) {
      // Validate domain on client side as well
      const userEmail = user.email || "";
      if (isAllowedEmail(userEmail)) {
        navigate(from, { replace: true });
      } else {
        toast.error("Only @superiorformulations.com.au accounts are allowed.");
      }
    }
  }, [user, navigate, from, showUpdatePassword]);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter your email and password.");
      return;
    }

    if (!isAllowedEmail(email)) {
      toast.error("Only @superiorformulations.com.au accounts are allowed.");
      return;
    }

    try {
      setIsLoading(true);
      await signInWithEmail(email, password);
    } catch (error: any) {
      toast.error(error.message || "Invalid email or password.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!isAllowedEmail(email)) {
      toast.error("Only @superiorformulations.com.au accounts are allowed.");
      return;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast.success("Check your inbox for the password reset link.");
    } catch (error: any) {
      toast.error(error.message || "Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error("Please enter and confirm your new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Please make sure both passwords match.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) throw error;

      toast.success("Your password has been successfully updated.");
      
      setShowUpdatePassword(false);
      setNewPassword("");
      setConfirmPassword("");
      
      // Clear the hash from URL
      window.history.replaceState(null, "", window.location.pathname);
      
      navigate(from, { replace: true });
    } catch (error: any) {
      toast.error(error.message || "Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show update password form when user clicks reset link
  if (showUpdatePassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-accent p-3 sm:p-4 safe-area-top safe-area-bottom">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-card">
          <div className="pt-6 sm:pt-8 pb-3 sm:pb-4 px-4 sm:px-6 text-center border-b border-border/50">
            <img src={logo} alt="Superior Formulations" className="h-10 sm:h-12 mx-auto mb-2 sm:mb-3" />
            <p className="text-muted-foreground text-[10px] sm:text-xs">Manufacturing & Quality Management</p>
          </div>

          <CardHeader className="text-center pb-2 pt-4 sm:pt-6 px-4 sm:px-6">
            <CardTitle className="text-lg sm:text-xl">Set New Password</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Enter your new password below
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 sm:space-y-5 pb-6 sm:pb-8 px-4 sm:px-6">
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-sm">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-sm">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium shadow-md hover:shadow-lg transition-all"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Password"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-accent p-3 sm:p-4 safe-area-top safe-area-bottom">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-card">
          <div className="pt-6 sm:pt-8 pb-3 sm:pb-4 px-4 sm:px-6 text-center border-b border-border/50">
            <img src={logo} alt="Superior Formulations" className="h-10 sm:h-12 mx-auto mb-2 sm:mb-3" />
            <p className="text-muted-foreground text-[10px] sm:text-xs">Manufacturing & Quality Management</p>
          </div>

          <CardHeader className="text-center pb-2 pt-4 sm:pt-6 px-4 sm:px-6">
            <CardTitle className="text-lg sm:text-xl">Reset Password</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {resetEmailSent 
                ? "Check your email for the reset link"
                : "Enter your email to receive a reset link"
              }
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 sm:space-y-5 pb-6 sm:pb-8 px-4 sm:px-6">
            {resetEmailSent ? (
              <div className="text-center space-y-4">
                <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
                  <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="text-sm text-foreground">
                    We've sent a password reset link to <strong>{email}</strong>
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmailSent(false);
                  }}
                  className="w-full"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@superiorformulations.com.au"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium shadow-md hover:shadow-lg transition-all"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sign In
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-accent p-3 sm:p-4 safe-area-top safe-area-bottom">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-card">
        {/* Logo Header */}
        <div className="pt-6 sm:pt-8 pb-3 sm:pb-4 px-4 sm:px-6 text-center border-b border-border/50">
          <img src={logo} alt="Superior Formulations" className="h-10 sm:h-12 mx-auto mb-2 sm:mb-3" />
          <p className="text-muted-foreground text-[10px] sm:text-xs">Manufacturing & Quality Management</p>
        </div>

        <CardHeader className="text-center pb-2 pt-4 sm:pt-6 px-4 sm:px-6">
          <CardTitle className="text-lg sm:text-xl">Welcome Back</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Sign in with your company email
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 sm:space-y-5 pb-6 sm:pb-8 px-4 sm:px-6">
          <form onSubmit={handleEmailSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@superiorformulations.com.au"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm">Password</Label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                />
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium shadow-md hover:shadow-lg transition-all"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Access restriction notice */}
          <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl bg-muted/50 border border-border/30">
            <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm">
              <p className="font-medium text-foreground">Staff Only Access</p>
              <p className="text-muted-foreground mt-0.5 sm:mt-1 text-[10px] sm:text-xs leading-relaxed">
                Restricted to 
                <span className="font-medium text-foreground"> @superiorformulations.com.au </span>
                accounts only.
              </p>
            </div>
          </div>

          {/* Help text */}
          <p className="text-center text-[10px] sm:text-xs text-muted-foreground pt-1 sm:pt-2">
            Need access? Contact your system administrator.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
